import i18n from "../../i18n";import { Trans } from "react-i18next";import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./EmployeeDocuments.css";
import { resolveApiUrl } from "../../api/api";
import sessionDB from "../../utils/sessionDB";
import { resolveOnboardingActionConfig, isOnboardingFeatureUnavailableError, isGenericOnboardingErrorMessage, parseOnboardingErrorResponse, handleOnboardingApiError } from "./onboardingFeatureAccess";

const ACTION_ORDER = ["add", "update", "search", "download"];

const ACTIONS = {
  add: {
    title: "Add Employee Document",
    serviceCode: "HR04A",
    endpoint: "/saas/hrms/emp-documents/add",
    method: "POST"
  },
  update: {
    title: "Update Employee Document",
    serviceCode: "HR04U",
    endpoint: "/saas/hrms/emp-documents/update/{empId}",
    method: "PUT"
  },
  search: {
    title: "Search Employee Document By ID",
    serviceCode: "HR04S",
    endpoint: "/saas/hrms/emp-documents/search/{empId}",
    method: "GET"
  },
  download: {
    title: "Download Employee Document",
    serviceCode: "HR04DD",
    endpoint: "/saas/hrms/emp-documents/download/{empId}/{documentType}",
    method: "GET"
  }
};

const PPF_TYPES = ["Public Provident Fund", "Employee Provident Fund", "General Provident Fund"];
const DOWNLOAD_TYPES = [
{ value: "pan", labelKey: "onboarding.options.documentType.pan" },
{ value: "aadhaar", labelKey: "onboarding.options.documentType.aadhaar" },
{ value: "cv", labelKey: "onboarding.options.documentType.cv" },
{ value: "offer-letter", labelKey: "onboarding.options.documentType.offerLetter" }];


const PPF_TYPE_OPTION_KEYS = {
  "Public Provident Fund": "onboarding.options.ppfType.publicProvidentFund",
  "Employee Provident Fund": "onboarding.options.ppfType.employeeProvidentFund",
  "General Provident Fund": "onboarding.options.ppfType.generalProvidentFund"
};

const FILE_LABEL_KEYS = {
  panDocument: "onboarding.labels.panDocument",
  aadhaarDocument: "onboarding.labels.aadhaarDocument",
  cvDocument: "onboarding.labels.cv",
  offerLetterDocument: "onboarding.labels.offerLetter"
};

function getFileRules() {
  return {
    panDocument: { accept: ".pdf,.jpg,.jpeg,.png", hint: i18n.t("onboarding.options.fileHint.pdfJpgPng") },
    aadhaarDocument: { accept: ".pdf,.jpg,.jpeg", hint: i18n.t("onboarding.options.fileHint.pdfJpg") },
    cvDocument: { accept: ".pdf,.doc,.docx", hint: i18n.t("onboarding.options.fileHint.pdfDoc") },
    offerLetterDocument: { accept: ".pdf", hint: i18n.t("onboarding.options.fileHint.pdf") }
  };
}

function getFileLabel(name) {
  const key = FILE_LABEL_KEYS[name];
  return key ? i18n.t(key) : name;
}

function translatePpfTypeLabel(value) {
  const key = PPF_TYPE_OPTION_KEYS[value];
  return key ? i18n.t(key) : value;
}

function translateDocumentTypeLabel(option) {
  return option.labelKey ? i18n.t(option.labelKey) : option.value;
}

const FIELD_LABEL_KEYS = {
  empId: "common.employeeId",
  panNo: "onboarding.labels.panNumber",
  aadharNo: "onboarding.labels.aadhaarNumber",
  aadhaarNo: "onboarding.labels.aadhaarNumber",
  ppfNo: "onboarding.labels.ppfNumber",
  ppfType: "onboarding.labels.ppfType",
  pfNo: "onboarding.labels.pfNumber",
  uanNo: "onboarding.labels.uanNumber",
  documentType: "onboarding.labels.documentType"
};

function getFieldLabel(name) {
  const key = FIELD_LABEL_KEYS[name];
  return key ? i18n.t(key) : name;
}

function getFieldLabelsMap() {
  return Object.fromEntries(
    Object.keys(FIELD_LABEL_KEYS).map((name) => [name, getFieldLabel(name)])
  );
}

function getRuleMessage(errorKey, fieldName) {
  const fullKey = `onboarding.errors.${errorKey}`;
  return i18n.exists(fullKey)
    ? i18n.t(fullKey)
    : i18n.t("validation.fieldRequired", { field: getFieldLabel(fieldName) });
}

const MAX_FILE_BYTES = 5 * 1024 * 1024;

const INITIAL_FORM = {
  empId: "",
  panNo: "",
  aadharNo: "",
  aadhaarNo: "",
  ppfNo: "",
  ppfType: "",
  pfNo: "",
  uanNo: "",
  documentType: ""
};

const INITIAL_FILES = {
  panDocument: null,
  aadhaarDocument: null,
  cvDocument: null,
  offerLetterDocument: null
};

const REQUIRED_BY_ACTION = {
  add: ["empId"],
  update: ["empId"],
  search: ["empId"],
  download: ["empId", "documentType"]
};

const VALIDATED_FIELDS_BY_ACTION = {
  add: ["empId", "panNo", "aadharNo", "aadhaarNo", "ppfNo", "ppfType", "pfNo", "uanNo"],
  update: ["empId", "panNo", "aadharNo", "aadhaarNo", "ppfNo", "ppfType", "pfNo", "uanNo"],
  search: ["empId"],
  download: ["empId", "documentType"]
};

const RULES = {
  empId: (value) => {
    const str = String(value || "").trim();
    return !str ? getRuleMessage("empIdRequired", "empId") : !/^\d+$/.test(str) ? getRuleMessage("empIdMustBeNumeric", "empId") : "";
  },
  panNo: (value) => {
    const str = String(value || "").trim();
    return !str ? "" : !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(str) ? i18n.t("onboarding.errors.panFormat") : "";
  },
  aadharNo: (value) => {
    const str = String(value || "").trim();
    return !str ? "" : !/^\d{4}\s\d{4}\s\d{4}$/.test(str) ? i18n.t("onboarding.errors.aadhaarFormat") : "";
  },
  aadhaarNo: (value) => {
    const str = String(value || "").trim();
    return !str ? "" : !/^\d{4}\s\d{4}\s\d{4}$/.test(str) ? i18n.t("onboarding.errors.aadhaarFormat") : "";
  },
  ppfNo: (value) => {
    const str = String(value || "").trim();
    return !str ? "" : str.length > 50 ? i18n.t("onboarding.errors.max50Characters") : "";
  },
  ppfType: (value) => {
    const str = String(value || "").trim();
    return !str ? "" : !PPF_TYPES.includes(str) ? getRuleMessage("chooseValidPpfType", "ppfType") : "";
  },
  pfNo: (value) => {
    const str = String(value || "").trim();
    return !str ? "" : str.length > 50 ? i18n.t("onboarding.errors.max50Characters") : "";
  },
  uanNo: (value) => {
    const str = String(value || "").trim();
    return !str ? "" : !/^\d{12}$/.test(str) ? i18n.t("onboarding.errors.uanMustBe12Digits") : "";
  },
  documentType: (value) => {
    const str = String(value || "").trim();
    return !str ? i18n.t("onboarding.errors.documentTypeRequired") : "";
  }
};

function formatAadhaar(value) {
  const str = String(value || "");
  const digits = str.replace(/\D/g, "").slice(0, 12);
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
}

function formatPan(value) {
  const str = String(value || "");
  return str.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
}

function buildUrl(template, form) {
  const empIdStr = String(form?.empId || "").trim();
  const docTypeStr = String(form?.documentType || "").trim();
  return String(template || "")
  .replace("{empId}", encodeURIComponent(empIdStr))
  .replace("{documentType}", encodeURIComponent(docTypeStr));
}

function getNextAction(currentAction) {
  const currentIndex = ACTION_ORDER.indexOf(currentAction);
  return ACTION_ORDER[(currentIndex + 1) % ACTION_ORDER.length];
}

function getFileNameFromDisposition(headerValue) {
  const match = /filename="?([^"]+)"?/i.exec(headerValue || "");
  return match ? match[1] : null;
}

function readFileAsBase64(file) {
  if (!file) return Promise.resolve(undefined);
  if (file instanceof Blob || file instanceof File) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = String(event.target?.result || "");
        resolve(result.split(",")[1] || "");
      };
      reader.onerror = () => reject(new Error(i18n.t("onboarding.errors.unableToReadFile")));
      reader.readAsDataURL(file);
    });
  }
  if (typeof file === "object" && file.base64) {
    return Promise.resolve(file.base64);
  }
  return Promise.resolve(undefined);
}

function Field({ id, label, required, error, hint, children, span2 = false }) {
  return (
    <div className={`edoc-field${span2 ? " span2" : ""}`}>
      <label className={`edoc-label${required ? " required" : ""}`} htmlFor={id}>{label}</label>
      {children}
      {hint ? <p className="edoc-hint">{hint}</p> : null}
      <p className="edoc-error" role="alert">{error || ""}</p>
    </div>);

}

function FilePicker({ label, value, hint, inputRef, accept, onChange, onRemove, onDownload }) {
  return (
    <div className="edoc-file-card">
      <div className="edoc-file-copy">
        <div className="edoc-file-title">{label}</div>
        {hint ? <div className="edoc-file-hint">{hint}</div> : null}
      </div>
      {value ?
      <div className="edoc-file-selected">
          <span className="edoc-file-name">{value.name}</span>
          <div className="edoc-file-actions" style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
            {onDownload ?
          <button type="button" className="edoc-file-link" onClick={onDownload}><Trans i18nKey="formActions.viewDownload" /></button> :
          null}
            <button type="button" className="edoc-file-link" onClick={onRemove}><Trans i18nKey="formActions.remove" /></button>
          </div>
        </div> :

      <button type="button" className="edoc-file-button" onClick={() => inputRef.current?.click()}><Trans i18nKey="formActions.upload" /></button>
      }
      <input ref={inputRef} type="file" accept={accept} className="edoc-hidden-input" onChange={onChange} />
    </div>);

}

export default function EmployeeDocuments({ accessToken, tenantCode, onBack, onNext, onSuccess, breadcrumb, initialEmployeeId = "", isTabMode = false, isActive = true, saveTriggerRef }) {
  useEffect(() => {
    if (saveTriggerRef) {
      saveTriggerRef.current = {
        submit: handleSave
      };
    }
  });
  const [action, setAction] = useState("add");
  const [form, setForm] = useState(INITIAL_FORM);
  const [files, setFiles] = useState(INITIAL_FILES);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [banner, _setBanner] = useState(null);
  const setBanner = (val) => {
    _setBanner((prev) => {
      const next = typeof val === "function" ? val(prev) : val;
      if (next && next.type === "error") {
        if (isOnboardingFeatureUnavailableError(next.msg)) {
          return { type: "error", msg: i18n.t("onboarding.featureUnavailable") };
        }
        if (isGenericOnboardingErrorMessage(next.msg)) {
          return prev;
        }
      }
      return next;
    });
  };
  const panRef = useRef(null);
  const aadhaarRef = useRef(null);
  const cvRef = useRef(null);
  const offerRef = useRef(null);

  const requiredFields = useMemo(() => new Set(REQUIRED_BY_ACTION[action] || []), [action]);
  const validatedFields = useMemo(() => VALIDATED_FIELDS_BY_ACTION[action] || [], [action]);

  const fetchDocs = useCallback(async () => {
    if (!initialEmployeeId) return;
    try {
      const response = await fetch(resolveApiUrl(`/saas/hrms/emp-documents/search/${initialEmployeeId}`), {
        method: 'GET',
        headers: {
          'x-tenant-code': tenantCode || sessionDB.getTenantCode() || "",
          'x-tenant-id': String(sessionDB.getTenantId() || ""),
          'x-service-code': 'HR04S',
          authorization: `Bearer ${accessToken || sessionDB.getAccessToken()}`
        }
      });
      if (response.ok) {
        const body = await response.json();
        const candidates = [
        body?.data?.content,
        body?.data?.records,
        body?.data?.items,
        body?.data?.documents,
        body?.data?.data?.content,
        body?.data,
        body?.content,
        body?.records,
        body];

        const arrayCandidate = candidates.find((c) => Array.isArray(c));
        const list = arrayCandidate ? arrayCandidate : body?.data && typeof body.data === "object" ? [body.data] : [body];
        const record = list.reduce((acc, item) => item && typeof item === "object" ? { ...acc, ...item } : acc, {});

        const hasValidBase64 = (str) => typeof str === "string" && str.trim().length > 20 && str.trim() !== "null" && str.trim() !== "undefined";
        const hasTruthyFlag = (...values) =>
        values.some((v) => v === true || v === "true" || v === 1 || v === "Y" || v === "y");
        const firstNonEmptyString = (...values) => {
          for (const value of values) {
            if (typeof value === "string" && value.trim() && value.trim() !== "null" && value.trim() !== "undefined") {
              return value.trim();
            }
          }
          return null;
        };

        const restoreAttachment = ({ base64Candidates, nameCandidates, flagCandidates, defaultName, docType }) => {
          const base64 = base64Candidates.find(hasValidBase64);
          const fileName = firstNonEmptyString(...nameCandidates);
          const flagged = hasTruthyFlag(...flagCandidates);
          if (!base64 && !fileName && !flagged) return null;
          return {
            name: fileName || defaultName,
            isExisting: true,
            docType,
            base64: base64 || undefined
          };
        };

        const aadharVal = record.aadhaarNo || record.aadharNo || "";
        const formattedAadhaar = aadharVal ? formatAadhaar(aadharVal) : "";
        const hasRecordFields = record && Object.keys(record).length > 0 && (
        record.panNo || aadharVal || record.pfNo || record.uanNo || record.ppfNo || record.ppfType ||
        record.panDocBase64 || record.cvBase64 || record.offerLetterBase64 || record.panFileName ||
        record.hasPanDoc || record.empId);


        if (hasRecordFields) {
          setForm({
            empId: String(initialEmployeeId),
            panNo: record.panNo ? formatPan(record.panNo) : "",
            aadharNo: formattedAadhaar,
            aadhaarNo: formattedAadhaar,
            pfNo: record.pfNo || "",
            uanNo: record.uanNo || "",
            ppfType: record.ppfType || "",
            ppfNo: record.ppfNo || "",
            documentType: ""
          });
          setAction("update");
        }

        const panB64 = [record.panDocBase64, record.panDoc, record.panDocument].find(hasValidBase64);
        const aadharB64 = [record.aadharDocBase64, record.aadhaarDocBase64, record.aadharDoc, record.aadhaarDoc, record.aadhaarDocument].find(hasValidBase64);
        const cvB64 = [record.cvBase64, record.cvDoc, record.cvDocument].find(hasValidBase64);
        const offerB64 = [record.offerLetterBase64, record.offerLetterDoc, record.offerLetterDocument].find(hasValidBase64);

        const restoredFiles = {
          panDocument: restoreAttachment({
            base64Candidates: [record.panDocBase64, record.panDoc, record.panDocument, panB64],
            nameCandidates: [record.panFileName, record.panDocFileName, record.panDocumentName, record.panName],
            flagCandidates: [record.hasPanDoc, record.hasPan, record.panDocAvailable, record.panUploaded],
            defaultName: record.panNo ? `PAN_${record.panNo}.pdf` : "PAN_Document.pdf",
            docType: "pan"
          }),
          aadhaarDocument: restoreAttachment({
            base64Candidates: [record.aadharDocBase64, record.aadhaarDocBase64, record.aadharDoc, record.aadhaarDoc, record.aadhaarDocument, aadharB64],
            nameCandidates: [record.aadhaarFileName, record.aadharFileName, record.aadhaarDocFileName, record.aadharDocFileName, record.aadhaarDocumentName],
            flagCandidates: [record.hasAadhaarDoc, record.hasAadharDoc, record.hasAadhaar, record.hasAadhar, record.aadhaarDocAvailable, record.aadhaarUploaded],
            defaultName: aadharVal ? `Aadhaar_${String(aadharVal).replace(/\s/g, "")}.pdf` : "Aadhaar_Document.pdf",
            docType: "aadhaar"
          }),
          cvDocument: restoreAttachment({
            base64Candidates: [record.cvBase64, record.cvDoc, record.cvDocument, cvB64],
            nameCandidates: [record.cvFileName, record.cvDocFileName, record.cvDocumentName, record.cvName],
            flagCandidates: [record.hasCv, record.hasCvDoc, record.cvAvailable, record.cvUploaded],
            defaultName: "CV_Document.pdf",
            docType: "cv"
          }),
          offerLetterDocument: restoreAttachment({
            base64Candidates: [record.offerLetterBase64, record.offerLetterDoc, record.offerLetterDocument, offerB64],
            nameCandidates: [record.offerLetterFileName, record.offerLetterDocFileName, record.offerLetterName],
            flagCandidates: [record.hasOfferLetter, record.hasOfferLetterDoc, record.offerLetterAvailable, record.offerLetterUploaded],
            defaultName: "Offer_Letter.pdf",
            docType: "offer-letter"
          })
        };

        // If search omitted filenames/flags/base64, probe download endpoints
        const probeHeaders = {
          "x-tenant-code": tenantCode || sessionDB.getTenantCode() || "",
          "x-tenant-id": String(sessionDB.getTenantId() || ""),
          "x-service-code": ACTIONS.download.serviceCode,
          authorization: `Bearer ${accessToken || sessionDB.getAccessToken()}`
        };
        const probeSlots = [
        ["panDocument", "pan", restoredFiles.panDocument?.name || (record.panNo ? `PAN_${record.panNo}.pdf` : "PAN_Document.pdf")],
        ["aadhaarDocument", "aadhaar", restoredFiles.aadhaarDocument?.name || (aadharVal ? `Aadhaar_${String(aadharVal).replace(/\s/g, "")}.pdf` : "Aadhaar_Document.pdf")],
        ["cvDocument", "cv", restoredFiles.cvDocument?.name || "CV_Document.pdf"],
        ["offerLetterDocument", "offer-letter", restoredFiles.offerLetterDocument?.name || "Offer_Letter.pdf"]];


        await Promise.all(probeSlots.map(async ([fileKey, docType, defaultName]) => {
          if (restoredFiles[fileKey]) return;
          try {
            const url = resolveApiUrl(`/saas/hrms/emp-documents/download/${Number(initialEmployeeId)}/${docType}`);
            const res = await fetch(url, { method: "GET", headers: probeHeaders });
            if (!res.ok) return;
            const dispositionName = getFileNameFromDisposition(res.headers.get("content-disposition"));
            await res.blob();
            restoredFiles[fileKey] = {
              name: dispositionName || defaultName,
              isExisting: true,
              docType
            };
          } catch {

            // Document not present
          }}));

        setFiles(restoredFiles);
        if (Object.values(restoredFiles).some(Boolean)) {
          setAction("update");
        }
      }
    } catch (err) {
      console.error("Failed to load employee documents info", err);
    }
  }, [initialEmployeeId, tenantCode, accessToken]);

  useEffect(() => {
    if (!isActive) return;
    if (!initialEmployeeId) {
      setForm({
        empId: "",
        panNo: "",
        aadharNo: "",
        aadhaarNo: "",
        pfNo: "",
        uanNo: "",
        ppfType: "",
        ppfNo: "",
        documentType: ""
      });
      setAction("add");
      return;
    }
    setForm((prev) => ({ ...prev, empId: String(initialEmployeeId) }));
    fetchDocs();
  }, [isActive, initialEmployeeId, fetchDocs]);

  function resetFeedback() {
    setBanner(null);
  }

  function validateField(name, value, touchMap = touched) {
    if (!validatedFields.includes(name)) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
      return true;
    }
    const requiredMessage = requiredFields.has(name) && !String(value || "").trim() ? i18n.t("validation.fieldRequired", { field: getFieldLabel(name) }) : "";
    const ruleMessage = RULES[name] ? RULES[name](value || "") : "";
    const message = requiredMessage || ruleMessage;
    setErrors((prev) => ({ ...prev, [name]: touchMap[name] ? message : "" }));
    return !message;
  }

  function validateAll() {
    const nextTouched = {};
    const nextErrors = {};
    let valid = true;

    Object.keys(RULES).forEach((name) => {
      if (!validatedFields.includes(name)) {
        nextErrors[name] = "";
        return;
      }
      nextTouched[name] = true;
      const requiredMessage = requiredFields.has(name) && !String(form[name] || "").trim() ? i18n.t("validation.fieldRequired", { field: getFieldLabel(name) }) : "";
      const ruleMessage = RULES[name] ? RULES[name](form[name] || "") : "";
      const message = requiredMessage || ruleMessage;
      nextErrors[name] = message;
      if (message) {
        valid = false;
      }
    });

    setTouched(nextTouched);
    setErrors(nextErrors);
    return valid;
  }

  function handleActionChange(nextAction) {
    setAction(nextAction);
    setErrors({});
    setTouched({});
    resetFeedback();
  }

  function handleInputChange(event) {
    const { name, value } = event.target;
    const rawValue = String(value || "");
    const nextValue =
    name === "panNo" ? formatPan(rawValue) :
    name === "aadharNo" || name === "aadhaarNo" ? formatAadhaar(rawValue) :
    rawValue;

    setForm((prev) => ({
      ...prev,
      [name]: nextValue,
      ...(name === "aadharNo" ? { aadhaarNo: nextValue } : {}),
      ...(name === "aadhaarNo" ? { aadharNo: nextValue } : {})
    }));
    if (touched[name]) {
      validateField(name, nextValue);
    }
  }

  function handleInputBlur(event) {
    const { name, value } = event.target;
    const nextTouched = { ...touched, [name]: true };
    setTouched(nextTouched);
    validateField(name, value, nextTouched);
  }

  function handleFileChange(fileKey, event) {
    resetFeedback();
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setBanner({ type: "error", msg: i18n.t("onboarding.errors.fileExceeds5Mb", { fileName: file.name }) });
      event.target.value = "";
      return;
    }
    setFiles((prev) => ({ ...prev, [fileKey]: file }));
  }

  function handleFileRemove(fileKey) {
    setFiles((prev) => ({ ...prev, [fileKey]: null }));
    const refMap = {
      panDocument: panRef,
      aadhaarDocument: aadhaarRef,
      cvDocument: cvRef,
      offerLetterDocument: offerRef
    };
    if (refMap[fileKey]?.current) {
      refMap[fileKey].current.value = "";
    }
  }

  async function handleDocumentMutation() {
    if (!validateAll()) {
      return;
    }

    setLoading(true);
    resetFeedback();

    try {
      const empIdStr = String(form?.empId || "").trim();
      const panNoStr = String(form?.panNo || "").trim();
      const aadharRawStr = String(form?.aadharNo || form?.aadhaarNo || "").replace(/\s/g, "").trim();
      const ppfNoStr = String(form?.ppfNo || "").trim();
      const ppfTypeStr = String(form?.ppfType || "").trim();
      const pfNoStr = String(form?.pfNo || "").trim();
      const uanNoStr = String(form?.uanNo || "").trim();

      const actionConfig = resolveOnboardingActionConfig({
        submoduleNames: ["Employee Documents", "Documents"],
        action: action,
        fallback: ACTIONS[action] || ACTIONS.add
      });

      const getCleanFileB64 = async (fileObj) => {
        if (!fileObj) return undefined;
        const b64 = await readFileAsBase64(fileObj);
        return b64 && typeof b64 === "string" && b64.trim().length > 20 && b64.trim() !== "null" && b64.trim() !== "undefined" ?
        b64.trim() :
        undefined;
      };

      const payload = {
        empId: Number(empIdStr),
        panNo: panNoStr || undefined,
        aadharNo: aadharRawStr || undefined,
        aadhaarNo: aadharRawStr || undefined,
        ppfNo: ppfNoStr || undefined,
        ppfType: ppfTypeStr || undefined,
        pfNo: pfNoStr || undefined,
        uanNo: uanNoStr || undefined,
        cvBase64: await getCleanFileB64(files.cvDocument),
        offerLetterBase64: await getCleanFileB64(files.offerLetterDocument),
        panDocBase64: await getCleanFileB64(files.panDocument),
        aadharDocBase64: await getCleanFileB64(files.aadhaarDocument),
        aadhaarDocBase64: await getCleanFileB64(files.aadhaarDocument)
      };
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined || payload[key] === "") {
          delete payload[key];
        }
      });

      const endpointTemplate = actionConfig.endpoint || ACTIONS[action]?.endpoint || ACTIONS.add.endpoint;
      const url = resolveApiUrl(buildUrl(endpointTemplate, form));
      console.log("pay:", JSON.stringify(payload));
      const response = await fetch(url, {
        method: actionConfig.method || ACTIONS[action]?.method || "POST",
        headers: {
          "Content-Type": "application/json",
          "x-tenant-code": tenantCode || sessionDB.getTenantCode() || "",
          "x-tenant-id": String(sessionDB.getTenantId() || ""),
          "x-service-code": actionConfig.serviceCode || ACTIONS[action]?.serviceCode || "HR04A",
          authorization: `Bearer ${accessToken || sessionDB.getAccessToken()}`
        },
        body: JSON.stringify(payload)
      });
      console.log("b", response.ok);
      if (!response.ok) {
        const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response, getFieldLabelsMap());
        handleOnboardingApiError(errorBody, errorMessage, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
        return;
      }

      const data = await response.json().catch(() => ({
        message: action === "add"
          ? i18n.t("onboarding.messages.addEmployeeDocumentCompleted")
          : i18n.t("onboarding.messages.updateEmployeeDocumentCompleted")
      }));
      setBanner({
        type: "success",
        msg: data.message || (action === "add"
          ? i18n.t("onboarding.messages.addEmployeeDocumentCompleted")
          : i18n.t("onboarding.messages.updateEmployeeDocumentCompleted"))
      });
      if (action === "update") {
        await fetchDocs();
      }
      if (onSuccess) {
        onSuccess(data);
      }
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
    } finally {
      setLoading(false);
    }
  }

  async function handleSearch() {
    if (!validateAll()) {
      return;
    }

    setLoading(true);
    resetFeedback();

    try {
      const actionConfig = resolveOnboardingActionConfig({
        submoduleNames: ["Employee Documents", "Documents"],
        action: "search",
        fallback: ACTIONS.search
      });
      const endpointTemplate = actionConfig.endpoint || ACTIONS.search.endpoint;
      const url = resolveApiUrl(buildUrl(endpointTemplate, form));
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-tenant-code": tenantCode || sessionDB.getTenantCode() || "",
          "x-tenant-id": String(sessionDB.getTenantId() || ""),
          "x-service-code": actionConfig.serviceCode || ACTIONS.search.serviceCode,
          authorization: `Bearer ${accessToken || sessionDB.getAccessToken()}`
        }
      });

      if (!response.ok) {
        const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response, getFieldLabelsMap());
        handleOnboardingApiError(errorBody, errorMessage, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
        return;
      }

      await response.json();
      setBanner({ type: "success", msg: i18n.t("onboarding.messages.employeeDocumentDetailsLoaded") });
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
    } finally {
      setLoading(false);
    }
  }

  async function handleDownload() {
    if (!validateAll()) {
      return;
    }

    setLoading(true);
    resetFeedback();

    try {
      const actionConfig = resolveOnboardingActionConfig({
        submoduleNames: ["Employee Documents", "Documents"],
        action: "download",
        fallback: ACTIONS.download
      });
      const endpointTemplate = actionConfig.endpoint || ACTIONS.download.endpoint;
      const url = resolveApiUrl(buildUrl(endpointTemplate, form));
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "x-tenant-code": tenantCode || sessionDB.getTenantCode() || "",
          "x-tenant-id": String(sessionDB.getTenantId() || ""),
          "x-service-code": actionConfig.serviceCode || ACTIONS.download.serviceCode,
          authorization: `Bearer ${accessToken || sessionDB.getAccessToken()}`
        }
      });

      if (!response.ok) {
        const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response, getFieldLabelsMap());
        handleOnboardingApiError(errorBody, errorMessage, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
        return;
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = getFileNameFromDisposition(response.headers.get("content-disposition")) || `${form.empId}-${form.documentType}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(objectUrl);
      setBanner({ type: "success", msg: i18n.t("onboarding.messages.documentDownloadStarted") });
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
    } finally {
      setLoading(false);
    }
  }

  function triggerFileDownload(docType, fileObj) {
    if (!fileObj) return;
    if (fileObj.base64) {
      const link = document.createElement("a");
      link.href = `data:application/octet-stream;base64,${fileObj.base64}`;
      link.download = fileObj.name || `${form.empId || initialEmployeeId}-${docType}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      return;
    }
    if (fileObj instanceof File) {
      const objectUrl = window.URL.createObjectURL(fileObj);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = fileObj.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      return;
    }
    const downloadForm = { empId: form.empId || initialEmployeeId, documentType: docType };
    const actionConfig = resolveOnboardingActionConfig({
      submoduleNames: ["Employee Documents", "Documents"],
      action: "download",
      fallback: ACTIONS.download
    });
    const url = resolveApiUrl(buildUrl(actionConfig.endpoint || ACTIONS.download.endpoint, downloadForm));
    fetch(url, {
      method: "GET",
      headers: {
        "x-tenant-code": tenantCode || sessionDB.getTenantCode() || "",
        "x-tenant-id": String(sessionDB.getTenantId() || ""),
        "x-service-code": actionConfig.serviceCode || ACTIONS.download.serviceCode,
        authorization: `Bearer ${accessToken || sessionDB.getAccessToken()}`
      }
    })
    .then((res) => {
      if (!res.ok) throw new Error(i18n.t("onboarding.errors.downloadFailed"));
      return res.blob();
    })
    .then((blob) => {
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${form.empId || initialEmployeeId}-${docType}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    })
    .catch(() => {
      setBanner({ type: "error", msg: i18n.t("onboarding.errors.unableToDownloadFile") });
    });
  }

  function handleSave() {
    if (action === "search") {
      handleSearch();
      return;
    }
    if (action === "download") {
      handleDownload();
      return;
    }
    handleDocumentMutation();
  }

  function handleNext() {
    const nextAction = getNextAction(action);
    handleActionChange(nextAction);
    if (onNext) {
      onNext(nextAction);
    }
  }

  return (
    <div className={`edoc-page${isTabMode ? " is-tab-mode" : ""}`}>
      {!isTabMode &&
      <header className="edoc-nav">
          <div className="edoc-nav-inner">
            <button className="edoc-back-top" onClick={onBack} aria-label={i18n.t("formActions.goBack")}>
              {"<"}
            </button>
            <span className="edoc-nav-title"><Trans i18nKey="onboarding.employeeDocuments" /></span>
          </div>
        </header>
      }

      <main className="edoc-main">
        {!isTabMode && breadcrumb &&
        <div className="edoc-breadcrumb" aria-label={i18n.t("common.breadcrumb")}>
            {breadcrumb.map((item, index) =>
          <React.Fragment key={item.label}>
                {index > 0 && <span className="edoc-bc-sep">/</span>}
                <button
              className={`edoc-bc-item${item.active ? " active" : ""}`}
              onClick={item.active ? undefined : item.onClick}
              disabled={item.active}>
              
                  {item.label}
                </button>
              </React.Fragment>
          )}
          </div>
        }

        {!isTabMode &&
        <div className="edoc-page-header">
            <div className="edoc-page-header-row">
              <div>
                <h1 className="edoc-page-title"><Trans i18nKey="onboarding.employeeDocuments" /></h1>
                <p className="edoc-page-sub"><Trans i18nKey="onboarding.managePanAadhaarPpfPfUanCvAnd" /></p>
              </div>
              <button className="edoc-header-action" type="button" onClick={() => handleActionChange(action === "update" ? "add" : "update")}>
                {action === "update" ? i18n.t("onboarding.addNew") : i18n.t("common.update")}
              </button>
            </div>
          </div>
        }

        <div className="edoc-form-card">
          <div className="edoc-section">
            <p className="edoc-section-title"><Trans i18nKey="onboarding.documentDetails" /></p>
            <div className="edoc-grid">
              <Field id="empId" label={getFieldLabel("empId")} required error={touched.empId && errors.empId}>
                <input
                  id="empId"
                  name="empId"
                  value={form.empId}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  className={`edoc-input${touched.empId && errors.empId ? " error" : ""}`}
                  placeholder={i18n.t("onboarding.enterEmployeeId")}
                  readOnly={Boolean(initialEmployeeId)} />
                
              </Field>

              {(action === "add" || action === "update") &&
              <>
                  <Field id="panNo" label={getFieldLabel("panNo")} required error={touched.panNo && errors.panNo}>
                    <input
                    id="panNo"
                    name="panNo"
                    value={form.panNo}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`edoc-input${touched.panNo && errors.panNo ? " error" : ""}`}
                    placeholder={i18n.t("onboarding.abcde1234f")} />
                  
                  </Field>

                  <Field id="aadharNo" label={getFieldLabel("aadharNo")} required error={touched.aadharNo && errors.aadharNo}>
                    <input
                    id="aadharNo"
                    name="aadharNo"
                    value={form.aadharNo}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`edoc-input${touched.aadharNo && errors.aadharNo ? " error" : ""}`}
                    placeholder={i18n.t("onboarding.xxxxXxxxXxxx")} />
                  
                  </Field>

                  <Field id="ppfNo" label={getFieldLabel("ppfNo")} required error={touched.ppfNo && errors.ppfNo}>
                    <input
                    id="ppfNo"
                    name="ppfNo"
                    value={form.ppfNo}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`edoc-input${touched.ppfNo && errors.ppfNo ? " error" : ""}`}
                    placeholder={i18n.t("onboarding.ppfAccountNumber")} />
                  
                  </Field>

                  <Field id="ppfType" label={getFieldLabel("ppfType")} required error={touched.ppfType && errors.ppfType}>
                    <div className="edoc-select-wrap">
                      <select
                      id="ppfType"
                      name="ppfType"
                      value={form.ppfType}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                      className={`edoc-select${touched.ppfType && errors.ppfType ? " error" : ""}`}>
                      
                        <option value=""><Trans i18nKey="onboarding.selectPpfType" /></option>
                        {PPF_TYPES.map((option) =>
                      <option key={option} value={option}>{translatePpfTypeLabel(option)}</option>
                      )}
                      </select>
                    </div>
                  </Field>

                  <Field id="pfNo" label={getFieldLabel("pfNo")} required error={touched.pfNo && errors.pfNo}>
                    <input
                    id="pfNo"
                    name="pfNo"
                    value={form.pfNo}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`edoc-input${touched.pfNo && errors.pfNo ? " error" : ""}`}
                    placeholder={i18n.t("onboarding.pfAccountNumber")} />
                  
                  </Field>

                  <Field id="uanNo" label={getFieldLabel("uanNo")} required error={touched.uanNo && errors.uanNo}>
                    <input
                    id="uanNo"
                    name="uanNo"
                    value={form.uanNo}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`edoc-input${touched.uanNo && errors.uanNo ? " error" : ""}`}
                    placeholder={i18n.t("onboarding.universalAccountNo")} />
                  
                  </Field>
                </>
              }

              {action === "download" &&
              <Field id="documentType" label={getFieldLabel("documentType")} required error={touched.documentType && errors.documentType} span2>
                  <div className="edoc-select-wrap">
                    <select
                    id="documentType"
                    name="documentType"
                    value={form.documentType}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    className={`edoc-select${touched.documentType && errors.documentType ? " error" : ""}`}>
                    
                      <option value=""><Trans i18nKey="onboarding.selectDocumentType" /></option>
                      {DOWNLOAD_TYPES.map((option) =>
                    <option key={option.value} value={option.value}>{translateDocumentTypeLabel(option)}</option>
                    )}
                    </select>
                  </div>
                </Field>
              }
            </div>
          </div>

          {(action === "add" || action === "update") &&
          <div className="edoc-section">
              <p className="edoc-section-title"><Trans i18nKey="common.attachments" /></p>
              <div className="edoc-file-grid">
                <FilePicker
                label={getFileLabel("panDocument")}
                value={files.panDocument}
                inputRef={panRef}
                accept={getFileRules().panDocument.accept}
                hint={getFileRules().panDocument.hint}
                onChange={(event) => handleFileChange("panDocument", event)}
                onRemove={() => handleFileRemove("panDocument")}
                onDownload={files.panDocument ? () => triggerFileDownload("pan", files.panDocument) : undefined} />
              
                <FilePicker
                label={getFileLabel("aadhaarDocument")}
                value={files.aadhaarDocument}
                inputRef={aadhaarRef}
                accept={getFileRules().aadhaarDocument.accept}
                hint={getFileRules().aadhaarDocument.hint}
                onChange={(event) => handleFileChange("aadhaarDocument", event)}
                onRemove={() => handleFileRemove("aadhaarDocument")}
                onDownload={files.aadhaarDocument ? () => triggerFileDownload("aadhaar", files.aadhaarDocument) : undefined} />
              
                <FilePicker
                label={getFileLabel("cvDocument")}
                value={files.cvDocument}
                inputRef={cvRef}
                accept={getFileRules().cvDocument.accept}
                hint={getFileRules().cvDocument.hint}
                onChange={(event) => handleFileChange("cvDocument", event)}
                onRemove={() => handleFileRemove("cvDocument")}
                onDownload={files.cvDocument ? () => triggerFileDownload("cv", files.cvDocument) : undefined} />
              
                <FilePicker
                label={getFileLabel("offerLetterDocument")}
                value={files.offerLetterDocument}
                inputRef={offerRef}
                accept={getFileRules().offerLetterDocument.accept}
                hint={getFileRules().offerLetterDocument.hint}
                onChange={(event) => handleFileChange("offerLetterDocument", event)}
                onRemove={() => handleFileRemove("offerLetterDocument")}
                onDownload={files.offerLetterDocument ? () => triggerFileDownload("offer-letter", files.offerLetterDocument) : undefined} />
              
              </div>
            </div>
          }

          <div className="edoc-section">
            {banner &&
            <div className={`edoc-banner ${banner.type}`} role="alert">
                {banner.msg}
              </div>
            }
            {!isTabMode &&
            <div className="edoc-actions">
                <button className="edoc-secondary-btn" type="button" onClick={onBack}><Trans i18nKey="formActions.back" /></button>
                <button className="edoc-primary-btn" type="button" onClick={handleSave} disabled={loading}>
                  {loading ? i18n.t("localConveyance.saving") : i18n.t("formActions.save")}
                </button>
                <button className="edoc-secondary-btn" type="button" onClick={handleNext}><Trans i18nKey="pagination.next2" /></button>
              </div>
            }
          </div>
        </div>

      </main>
    </div>);

}
